import Vue from 'vue';
import axios from 'axios';
import VueAxios from 'vue-axios';
import store from '@/store';
import router from '@/router';
import Swal from 'sweetalert2';

import { loadProgressBar } from 'axios-progress-bar';
import 'axios-progress-bar/dist/nprogress.css';
loadProgressBar();

axios.defaults.baseURL = '/api';
if (process.env.VUE_APP_LARAVEL_API_URL) {
  axios.defaults.baseURL = process.env.VUE_APP_LARAVEL_API_URL;
}
axios.defaults.headers.common['Accept'] = 'application/json';

var token = localStorage.getItem('token');
if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

axios.interceptors.response.use(
  function (response) {
    if (response?.headers['x-maintenance']) {
      // tell the store we have a maintenance notice to display
      store.commit('maintenance', JSON.parse(response?.headers['x-maintenance']));
    }
    return response;
  },
  function (error) {
    const status = error.response?.status;
    const errorData = error.response?.data;
    const baseErrorMessage = "There was an issue with your request. If the problem persists, please contact support.";
    const errorMessage = errorData?.message || baseErrorMessage;

    // check for network error
    if (!error.response) {
      // network error (e.g., user is offline)
      const offlineMessage = "Looks like you are offline. Please try again after reestablishing your internet connection.";
      reportErrors([[offlineMessage]], 'Network Error', null);
      return Promise.reject(error);
    }

    // mapping for error messages and actions
    const errorMapping = {
      400: {
        message: errorData?.message || "There was an issue with your request. Please check your input and try again. If the problem persists, please contact support.",
        type: 'Bad request'
      },
      401: {
        handle: () => {
          console.log('401 interceptor checking config url', error.config.url);
          if (error.config.url === '/auth/signout') {
            console.log('401 while trying to sign out, just returning success');
            return Promise.resolve();
          }
          if (error.config.url === '/auth/signin') {
            return Promise.reject(error);
          }
          if (error.config.url === '/auth/refresh') {
            console.log('401 interceptor, proceeding with logout');
            store.dispatch('logout');
            router.push('/');
            // return a rejection
            return Promise.reject(error);
          }
          // refresh token and replay the request
          return store.dispatch('getNewToken')
            .then(token => {
              // new request with new token
              const config = error.config;
              config.headers['Authorization'] = `Bearer ${token}`;

              // retry the request
              return new Promise((resolve, reject) => {
                axios
                  .request(config)
                  .then(response => {
                    resolve(response);
                  })
                  .catch(error => {
                    reject(error);
                  });
              });
            })
            .catch(error => {
              console.log('Token refresh failed, logging out');
              store.dispatch('logout');
              Promise.reject(error);
            });
        },
        //type: 'Unauthorized'
      },
      403: {
        message: "You do not have permission to access this resource. If you think this is a mistake, please contact support.",
        type: 'Unauthorized Access'
      },
      404: { 
        // redirect to /404 page
        handle: () => Promise.reject(error.response)
      },
      405: {
        message: errorData?.message || "The HTTP method used is not allowed for this resource. If the problem persists, please contact support.",
        type: 'Method Not Allowed'
      },
      406: {
        message: errorData?.message || "The resource cannot produce a response that matches the criteria. If the problem persists, please contact support.",
        type: 'Not Acceptable'
      },
      407: {
        message: errorData?.message || "You need to authenticate with the proxy server. If the problem persists, please contact support.",
        type: 'Proxy Authentication Required'
      },
      408: {
        message: errorData?.message || "The server timed out waiting for the request. If the problem persists, please contact support.",
        type: 'Request Timeout'
      },
      409: {
        message: errorData?.message || "The request could not be completed due to a conflict. If the problem persists, please contact support.",
        type: 'Conflict'
      },
      410: {
        message: errorData?.message || "The resource is no longer available and has been permanently removed. If the problem persists, please contact support.",
        type: 'Gone'
      },
      411: { 
        message: errorData?.message || "The request did not specify the length of its content. If the problem persists, please contact support.",
        type: 'Length Required'
      },
      412: { 
        message: errorData?.message || "A precondition set by the server was not met. If the problem persists, please contact support.",
        type: 'Precondition Failed'
      },
      413: { 
        message: errorData?.message || "The request payload is too large to be processed. If the problem persists, please contact support.",
        type: 'Payload Too Large'
      },
      414: { 
        message: errorData?.message || "The request URI is too long for the server to process. If the problem persists, please contact support.",
        type: 'URI Too Long'
      },
      415: { 
        message: errorData?.message || "The media type of the request is not supported. If the problem persists, please contact support.",
        type: 'Unsupported Media Type'
      },
      416: { 
        message: errorData?.message || "The request range is not satisfiable for the resource. If the problem persists, please contact support.",
        type: 'Range Not Satisfiable'
      },
      417: { 
        message: errorData?.message || "The server could not meet the expectations specified in the request. If the problem persists, please contact support.",
        type: 'Expectation Failed'
      },
      418: { 
        message: errorData?.message || "The server is a teapot and cannot brew coffee. If the problem persists, please contact support.",
        type: 'I\'m a teapot'
      },
      421: { 
        message: errorData?.message || "The request was directed to a server that cannot produce a response. If the problem persists, please contact support.",
        type: 'Misdirected Request'
      },
      422: { 
        message: errorData?.message || "The request was well-formed but unable to be processed. If the problem persists, please contact support.",
        type: 'Unprocessable Entity'
      },
      423: { 
        message: errorData?.message || "The resource is locked and cannot be modified. If the problem persists, please contact support.",
        type: 'Locked'
      },
      424: { 
        message: errorData?.message || "The request failed due to a failure of a previous request. If the problem persists, please contact support.",
        type: 'Failed Dependency'
      },
      425: { 
        message: errorData?.message || "The server is unwilling to risk processing a request that might be replayed. If the problem persists, please contact support.",
        type: 'Too Early'
      },
      426: { 
        message: errorData?.message || "The server requires a different protocol to be used. If the problem persists, please contact support.",
        type: 'Upgrade Required'
      },
      428: { 
        message: errorData?.message || "The server requires a precondition to be met. If the problem persists, please contact support.",
        type: 'Precondition Required'
      },
      429: { 
        message: errorData?.message || "You have sent too many requests in a given amount of time. If the problem persists, please contact support.",
        type: 'Too Many Requests'
      },
      431: { 
        message: errorData?.message || "The request headers are too large to process. If the problem persists, please contact support.",
        type: 'Request Header Fields Too Large'
      },
      451: { 
        message: errorData?.message || "The resource is unavailable due to legal reasons. If you think this is a mistake, please contact support.",
        type: 'Unavailable For Legal Reasons'
      },
      501: { 
        message: errorData?.message || "The server does not support the functionality required. If you think this is a mistake, please contact support.",
        type: 'Not Implemented'
      },
      502: { 
        message: errorData?.message || "The server received an invalid response from an upstream server. If the problem persists, please contact support.",
        type: 'Bad Gateway'
      },
      503: { 
        message: errorData?.message || "The server is currently unable to handle the request. If the problem persists, please contact support.",
        type: 'Service Unavailable'
      },
      504: { 
        message: errorData?.message || "The server did not receive a timely response from an upstream server. If the problem persists, please contact support.",
        type: 'Gateway Timeout'
      },
      505: { 
        message: errorData?.message || "The server does not support the HTTP protocol version. If the problem persists, please contact support.",
        type: 'HTTP Version Not Supported'
      },
      506: { 
        message: errorData?.message || "The server has an internal configuration error. If the problem persists, please contact support.",
        type: 'Variant Also Negotiates'
      },
      507: { 
        message: errorData?.message || "The server cannot store the representation needed to complete the request. If the problem persists, please contact support.",
        type: 'Insufficient Storage'
      },
      508: { 
        message: errorData?.message || "The server detected an infinite loop while processing the request. If the problem persists, please contact support.",
        type: 'Loop Detected'
      },
      509: { 
        message: errorData?.message || "The server has exceeded its bandwidth limit. If the problem persists, please contact support.",
        type: 'Bandwidth Limit Exceeded'
      },
      510: { 
        message: errorData?.message || "Further extensions to the request are required for the server to fulfill it. If the problem persists, please contact support.",
        type: 'Not Extended'
      },
      511: { 
        message: errorData?.message || "Authentication is required to access the network. If the problem persists, please contact support.",
        type: 'Network Authentication Required'
      },
      500: {
        message: errorData?.message || "We couldn't complete your request due to an internal error. If the problem persists, please contact support.",
        type: 'Internal Server Error'
      }
    };

    if (errorMapping[status]) {
      const { message: mappedMessage, type, handle } = errorMapping[status];
      if (handle) {
        return handle();
      }
      if (type) {
        reportErrors(Object.values(errorData?.errors || {}), type, status);
      } else {
        reportErrors([[mappedMessage]], type, status);
      }
    } else {
      reportErrors([[errorMessage]], 'Oops!', status);
    }
    return Promise.reject(error);
  }
);

/*
function checkIfTokenIsExpired() {
  let dateNow = new Date();
  return decodedToken.exp < dateNow.getTime();
}
*/

/* Given an array of errors (with erroSets), display them in a toast (on screen)*/
function reportErrors(errs, title, status, variant = 'danger') {
  if (errs.length == 1 && errs[0].length == 1) {
    Swal.fire({
      html: `
        <div class="" style="margin: 0;">
          <div class="row">
            <div class="col-12 px-0">
              <div class="card-header" style="padding: 0 0.5rem;">
                <div style="display: flex; justify-content: space-between;">
                  <span class="fw-bold">${status ? `Error ${status}!` : 'Oops! Something went wrong.'}</span>
                  <button id="close-button"
                  style="background: none; border: none; cursor: pointer; font-weight: 600; font-size: 25px; padding-bottom: 6px; margin-top: -5px; color: black !important;">
                    &times;
                  </button>
                </div>
              </div>
              <div class="card-body p-1 pt-4" style="display: flex; justify-content: space-between;">
                <p class="text-start me-3">${errs[0][0]}</p>
                ${status !== null ? `<img src="/img/errors/${status ? status : 'undefined'}.png" alt="error-${status ? status : 'undefined'}" class="m-auto" width="200px" height="109px" />` : ''}
              </div>
              <div class="text-start border-top-0">
                <button id="support-button" class="btn btn-primary" style="padding: 0.425rem 1.7rem !important; width: fit-content !important;">
                  Support
                </button>
              </div>
            </div>
          </div>
        </div>
      `,
      width: 650,
      showConfirmButton: false,
      showCancelButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        const closeButton = document.getElementById('close-button');
        const supportButton = document.getElementById('support-button');
        closeButton.addEventListener('click', () => {
          Swal.close();
        });
        supportButton.addEventListener('click', () => {
          Swal.close();
          Vue.bus.emit('show-support-form');
        });
      },
    });
  } else {
    errs.forEach(errorSet => {
      let message = '';
      errorSet.forEach(error => {
        message += error + ' ';
      });

      store.commit('toast/showToast', {
        message,
        title,
        variant,
        autohide: false,
        //position: 'top-50 start-50 translate-middle',
      });
    });
  }
}

Vue.use(VueAxios, axios);
export default axios;
